/* eslint-disable camelcase */
import { List } from 'immutable'
import { call, put, select, take, takeEvery } from 'redux-saga/effects'
import { get } from 'lodash-es'
import axios from 'axios'
import { normalize } from 'normalizr'

import queryParser from '../util/queryParser'

import { makeGetFullUserByUsername } from './entities'
import { emptyPicks, requestAccountPicks } from './pick'
import { resetContentPlainObject, requestCollections, requestPins } from './userContent'
import { selectLocations } from './routerLocations'
import { selectUser } from './user'
import { collection } from '../schemas'

import API from '../config'
import { getHeaders } from './api'

// ------------------------------------
// Constants
// ------------------------------------

const rootApi = `${API.apiPath}/${API.apiVersion}/`

const REQUEST_ADD_TAG = 'REQUEST_ADD_TAG'
const ADD_TAG = 'ADD_TAG'
const ADD_TAGS = 'ADD_TAGS'
const REMOVE_TAG = 'REMOVE_TAG'
const EMPTY_TAGS = 'EMPTY_TAGS'
const REQUEST_UPDATE_CARD_TAGS = 'REQUEST_UPDATE_CARD_TAGS'
export const RECEIVE_CARD_WITH_UPDATED_TAGS = 'RECEIVE_CARD_WITH_UPDATED_TAGS'

// ------------------------------------
// Actions
/* ------------------------------------ */

export const requestAddTag = (text, isRemovable = false) => ({
  type: REQUEST_ADD_TAG,
  payload: { text, isRemovable }
})

export const requestUpdateCardTags = (id, tags = [], isMedia) => ({
  type: REQUEST_UPDATE_CARD_TAGS,
  payload: { id, tags, isMedia }
})

export const addTag = payload => ({
  type: ADD_TAG,
  payload
})

const addTags = payload => ({
  type: ADD_TAGS,
  payload
})

export const removeTag = payload => ({
  type: REMOVE_TAG,
  payload
})

export const emptyTags = () => ({
  type: EMPTY_TAGS
})

const receiveCardWithUpdatedTags = data => ({
  type: RECEIVE_CARD_WITH_UPDATED_TAGS,
  payload: { data }
})

function* processChangeTags(action) {
  const { text, isRemovable } = get(action, 'payload', {})
  const tagsBefore = yield select(selectTags)

  if ((isRemovable || !tagsBefore.includes(text)) && text !== '...') {
    window.scrollTo(0, 0)
    if (action.payload) {
      if (typeof text === 'string') {
        if (isRemovable) {
          yield put(removeTag(text))
        } else {
          yield put(addTag(text))
        }
      } else {
        yield put(addTags(text))
      }
    }

    const locations = yield select(selectLocations)
    const location = locations[locations.length - 1]
    const segments = location.pathname.substr(1).split('/')

    if (segments[0]) {
      if (segments[0].startsWith('@')) {
        const getUser = makeGetFullUserByUsername()
        const user = yield select(getUser, segments[0].startsWith('@') && segments[0].replace('@', ''))
        const collectionThumbnails = queryParser(location.search, 'thumbnail') || 'squared'

        if (segments[1]) {
          if (segments[1] === 'picks') {
            if (user) {
              yield put(resetContentPlainObject(user.id))
              yield put(emptyPicks())
              yield put(requestAccountPicks(user.id, null, { refId: user.id }))
              yield put(requestPins({ id: user.id, feed: 'picks' }))
            }
          }

          if (segments[1] === 'collections') {
            if (user) {
              yield put(resetContentPlainObject(user.id))
              yield put(requestCollections({ id: user.id, published: true }))
              yield put(requestPins({ id: user.id, feed: 'collections', collectionThumbnails }))
            }
          }
        } else {
          if (user) {
            yield put(emptyPicks())
            yield put(requestAccountPicks(user.id, null, { refId: user.id }))
            yield put(resetContentPlainObject(user.id))
            yield put(requestCollections({ id: user.id, published: true }))
            yield put(requestPins({ id: user.id, feed: 'picks' }))
            yield put(requestPins({ id: user.id, feed: 'collections', collectionThumbnails }))
          }
        }
      }
    }
  }
}

function* processUpdateCardTags(action) {
  const user = yield select(selectUser)
  const { id, tags, isMedia } = action.payload
  const token = get(user, 'data.auth_token')
  const headers = getHeaders(token)
  const response = yield call(
    [axios, 'post'],
    isMedia ? 'deck/update-card-media-tags' : 'deck/update-card-tags',
    { card_id: id, [isMedia ? 'media_tags' : 'tags']: tags },
    { baseURL: rootApi, headers }
  )
  if (get(response, 'status') === 200) {
    const normalizedCard = normalize(response.data.card, collection)
    yield put(receiveCardWithUpdatedTags(normalizedCard))
  }
}

export function* addSaga() {
  while (true) {
    const action = yield take(REQUEST_ADD_TAG)
    yield call(processChangeTags, action)
  }
}

export function* emptySaga() {
  while (true) {
    yield take(EMPTY_TAGS)
    yield call(processChangeTags, {})
  }
}

function* updateCardTagsSaga() {
  yield takeEvery(REQUEST_UPDATE_CARD_TAGS, processUpdateCardTags)
}

export const sagas = [addSaga, emptySaga, updateCardTagsSaga]

// ------------------------------------
// Selects
/* ------------------------------------ */

export const selectTags = getState => getState.tags.toJS()

// ------------------------------------
// Reducer
/* ------------------------------------ */

const initialState = new List()

export default function(state = initialState, { type, payload }) {
  switch (type) {
    case ADD_TAG: {
      return !(state.indexOf(payload) + 1) ? state.push(payload) : state
    }
    case ADD_TAGS: {
      return state.push(...payload)
    }
    case REMOVE_TAG: {
      return state.filterNot(tag => tag === payload)
    }
    case EMPTY_TAGS:
      return initialState
    default: {
      return state
    }
  }
}
